// Decide whether a stage_presence_logs row belongs to a given
// assignment.
//
// History
// -------
// Pre-FK era this was a label-string bridge: assignment.stage_name
// (producer-editable) ↔ festival_areas.name (polygon-side). Strict
// equality silently broke on rename — the Geheime Liefde "0%
// attendance for everything except Mainstage" symptom that motivated
// migration `20260503150000_festival_areas_briefing_stage_fk.sql`.
//
// Post-FK (migration `20260504240000_stage_id_canonical.sql`)
// ------------------------------------------------------------
// Both sides now carry a stable UUID FK to `briefing_stages.id`:
//   * `crew_assignments.stage_id` (filled by BEFORE INSERT trigger
//      from label match; also self-heals NULL → UUID on next save)
//   * `festival_areas.briefing_stage_id` (auto-linked by
//      `set_festival_area_briefing_stage_fk` BEFORE trigger).
//
// Match strategy now:
//   1. **FK-fast-path** — if both sides expose the UUID, compare
//      directly. Rename-stable. Skips every legacy bridge.
//   2. **JSONB-bridge fallback** — for rows where one side is still
//      NULL (older briefings, or the 244 `assignment_type='custom'`
//      rows that have no real stage), bridge through
//      `briefing.stages[].name → label` to match against the
//      polygon's legacy `stage_id` slug.
//   3. **Last-resort name equality** — for the truly legacy case
//      where neither FK nor JSONB bridge resolves.

type Stage = { name?: string | null; label?: string | null };

interface Briefing {
  stages?: Stage[] | null | unknown;
}

interface PresenceArea {
  name: string | null;
  stage_id: string | null;
  briefing_stage_id?: string | null;
}

export function matchAssignmentToPresence({
  assignmentStageName,
  assignmentStageId,
  presenceArea,
  briefing,
}: {
  assignmentStageName: string;
  /**
   * `crew_assignments.stage_id` UUID. When both sides have an FK the
   * helper short-circuits on a UUID compare and ignores everything
   * else.
   */
  assignmentStageId?: string | null;
  presenceArea: PresenceArea | null | undefined;
  briefing: Briefing | null | undefined;
}): boolean {
  if (!presenceArea) return false;

  // 1. FK fast path. The post-canonical world: both sides hold a
  // UUID pointing at the same `briefing_stages` row.
  if (assignmentStageId && presenceArea.briefing_stage_id) {
    return assignmentStageId === presenceArea.briefing_stage_id;
  }

  // 2. JSONB bridge for legacy rows where the FK hasn't backfilled
  // yet (or for the assignment_type='custom' subset that never gets
  // a real stage_id).
  const stages = Array.isArray(briefing?.stages) ? (briefing!.stages as Stage[]) : null;
  const mappedStageId = stages?.find((s) => s?.label === assignmentStageName)?.name ?? null;

  if (mappedStageId) {
    return presenceArea.stage_id === mappedStageId;
  }

  // 3. Last-resort name equality. Assignment is unmapped (custom
  // free-text subject, or briefing.stages missing entirely).
  return presenceArea.name === assignmentStageName;
}
